import { useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MailCheck, X } from 'lucide-react';

/**
 * Sticky top banner shown to email-auth users who haven't verified their email.
 * Provides a 6-digit code input + resend link. Dismissed only after verify succeeds.
 */
export default function EmailVerificationBanner() {
  const { user, refresh } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState('');
  const [dismissed, setDismissed] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  if (user.auth_provider && user.auth_provider !== 'email') return null;
  if (dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/resend_verification');
      if (r.data?.mock_code) setDevCode(r.data.mock_code);
      toast.success('Verification code sent to your email');
      setExpanded(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length < 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await api.post('/auth/verify_email', { code });
      toast.success('Email verified — withdrawals unlocked');
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#F0B90B]/10 border-b border-[#F0B90B]/40 px-4 py-2 text-sm" data-testid="email-verify-banner">
      <div className="max-w-[1440px] mx-auto flex flex-wrap items-center gap-3">
        <MailCheck className="w-4 h-4 text-[#F0B90B] shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[#F0B90B] font-semibold">Verify your email</span>
          <span className="text-[#B7BDC6] ml-2 hidden sm:inline">
            Withdrawals are blocked until you confirm <span className="font-mono text-white">{user.email}</span>.
          </span>
        </div>
        {!expanded ? (
          <>
            <Button size="sm" onClick={() => setExpanded(true)} data-testid="verify-open-btn"
              className="bg-[#F0B90B] hover:bg-[#FCD535] text-[#0B0E11] rounded-sm h-8 text-xs font-semibold">
              Enter code
            </Button>
            <Button size="sm" variant="outline" onClick={resend} disabled={busy} data-testid="verify-resend-btn"
              className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm h-8 text-xs">
              {busy ? 'Sending…' : 'Resend'}
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <Input value={code} onChange={e=>setCode(e.target.value.replace(/[^0-9]/g,''))}
              placeholder="123456" maxLength={6} inputMode="numeric"
              className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-8 text-white font-mono text-sm w-28 text-center focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
              data-testid="verify-code-input" />
            <Button size="sm" onClick={verify} disabled={busy || code.length < 6} data-testid="verify-submit-btn"
              className="bg-[#0ECB81] hover:bg-[#0BB574] text-white rounded-sm h-8 text-xs font-semibold">
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
            <button onClick={resend} disabled={busy} className="text-[#FCD535] text-xs hover:underline" data-testid="verify-resend-inline">Resend</button>
          </div>
        )}
        <button onClick={() => setDismissed(true)} className="text-[#848E9C] hover:text-white p-1" title="Dismiss" data-testid="verify-dismiss-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {devCode && (
        <div className="max-w-[1440px] mx-auto mt-1 text-[10px] font-mono text-[#F0B90B] pl-7" data-testid="verify-dev-code">
          Dev mode code: <span className="font-bold">{devCode}</span> (email sender domain not verified — use this)
        </div>
      )}
    </div>
  );
}
